import React, { useEffect, useState } from 'react';
import { api } from '../services/api';
import { useAgentAlias } from '../services/agentAliasContext';
import RecordingDetail from '../components/RecordingDetail';

interface AliasItem {
  aliasValue: string;
  aliasType: string;
  createdAt: string;
}

interface AgentGroup {
  canonicalName: string;
  aliases: AliasItem[];
}

function guessAliasType(value: string): string {
  if (/^\d{8,10}$/.test(value)) return 'npn';
  if (value.includes('@')) return 'email';
  if (/^[0-9a-f-]{36}$/i.test(value)) return 'uuid';
  if (/^[a-z]+-[a-z]+/.test(value)) return 'name';
  return 'other';
}

const ALIAS_TYPES = [
  { value: 'npn', label: 'NPN' },
  { value: 'email', label: 'Email' },
  { value: 'ghl_location', label: 'GHL Location' },
  { value: 'vicidial', label: 'Vicidial ID' },
  { value: 'pulsedial', label: 'PulseDial ID' },
  { value: 'dialedin', label: 'DialedIn ID' },
  { value: 'name', label: 'Name' },
  { value: 'uuid', label: 'UUID' },
  { value: 'other', label: 'Other' },
];

const TYPE_COLORS: Record<string, { bg: string; text: string }> = {
  npn: { bg: '#e8f5e9', text: '#2e7d32' },
  email: { bg: '#e3f2fd', text: '#1565c0' },
  ghl_location: { bg: '#e8eaf6', text: '#283593' },
  vicidial: { bg: '#fff3e0', text: '#e65100' },
  pulsedial: { bg: '#e0f7fa', text: '#00838f' },
  dialedin: { bg: '#fce4ec', text: '#ad1457' },
  name: { bg: '#f3e5f5', text: '#6a1b9a' },
  uuid: { bg: '#fce4ec', text: '#c62828' },
  other: { bg: '#f5f5f5', text: '#616161' },
};

export default function AgentAliases() {
  const { refreshMap } = useAgentAlias();
  const [agents, setAgents] = useState<AgentGroup[]>([]);
  const [unmapped, setUnmapped] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);
  const [newAgentName, setNewAgentName] = useState('');
  const [creatingFrom, setCreatingFrom] = useState<string | null>(null);
  const [addAliasFor, setAddAliasFor] = useState<string | null>(null);
  const [manualAlias, setManualAlias] = useState('');
  const [manualAliasType, setManualAliasType] = useState('other');
  const [newAgentAliasType, setNewAgentAliasType] = useState('other');
  const [previewingId, setPreviewingId] = useState<string | null>(null);
  const [previewRecordings, setPreviewRecordings] = useState<any[]>([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [selectedRecording, setSelectedRecording] = useState<any>(null);

  const loadData = () => {
    setLoading(true);
    api.getAgentAliases()
      .then((data: any) => {
        setAgents(data.agents || []);
        setUnmapped(data.unmapped || []);
      })
      .catch((err: any) => setMessage({ text: err.message, type: 'error' }))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadData(); }, []);

  const showMessage = (text: string, type: 'success' | 'error') => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 4000);
  };

  const handleCreateAlias = async (aliasValue: string, canonicalName: string, aliasType?: string) => {
    try {
      await api.createAgentAlias(aliasValue, canonicalName, aliasType || guessAliasType(aliasValue));
      showMessage(`Mapped "${aliasValue}" to ${canonicalName}`, 'success');
      refreshMap();
      loadData();
    } catch (err: any) {
      showMessage(err.message, 'error');
    }
  };

  const handleDeleteAlias = async (aliasValue: string) => {
    try {
      await api.deleteAgentAlias(aliasValue);
      showMessage(`Removed alias "${aliasValue}"`, 'success');
      refreshMap();
      loadData();
    } catch (err: any) {
      showMessage(err.message, 'error');
    }
  };

  const handleCreateAgent = async (aliasValue: string) => {
    if (!newAgentName.trim()) return;
    await handleCreateAlias(aliasValue, newAgentName.trim(), newAgentAliasType);
    setNewAgentName('');
    setNewAgentAliasType('other');
    setCreatingFrom(null);
  };

  const handleAddManualAlias = async (canonicalName: string) => {
    if (!manualAlias.trim()) return;
    await handleCreateAlias(manualAlias.trim(), canonicalName, manualAliasType);
    setManualAlias('');
    setManualAliasType('other');
    setAddAliasFor(null);
  };

  // Title-case for pre-filling new agent name
  const titleCase = (s: string) => s.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

  const togglePreview = async (id: string) => {
    if (previewingId === id) {
      setPreviewingId(null);
      setPreviewRecordings([]);
      return;
    }
    setPreviewingId(id);
    setPreviewRecordings([]);
    setPreviewLoading(true);
    try {
      const data = await api.searchPaginated({
        agent: id,
        dateFrom: '2020-01-01',
        dateTo: '2030-12-31',
        pageSize: 3,
        skipCount: 'true',
      });
      setPreviewRecordings(data.recordings || []);
    } catch (err: any) {
      showMessage(`Could not load recordings: ${err.message}`, 'error');
    } finally {
      setPreviewLoading(false);
    }
  };

  const formatDuration = (s: number) => {
    if (!s) return '-';
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };
  const formatPhone = (r: any) => {
    const d = r.callerPhone || '';
    if (d.length === 11 && d[0] === '1') return `(${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7)}`;
    if (d.length === 10) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
    return d || r.callerId || '-';
  };

  // Sort agents by last name (last whitespace-separated token), then by full name as tiebreaker
  const sortedAgents = [...agents].sort((a, b) => {
    const lastA = (a.canonicalName.trim().split(/\s+/).pop() || '').toLowerCase();
    const lastB = (b.canonicalName.trim().split(/\s+/).pop() || '').toLowerCase();
    return lastA.localeCompare(lastB) || a.canonicalName.localeCompare(b.canonicalName);
  });

  return (
    <div>
      <div className="page-header">
        <h1>Agent Aliases</h1>
        <p>Map recording identifiers (NPNs, emails, IDs) to canonical agent names. Pick an existing agent from the dropdown or create a new one.</p>
      </div>

      {message && (
        <div style={{ padding: '12px 16px', marginBottom: 16, borderRadius: 6,
          background: message.type === 'success' ? '#e8f5e9' : '#ffebee',
          color: message.type === 'success' ? '#2e7d32' : '#c62828', fontSize: 13 }}>
          {message.text}
        </div>
      )}

      {loading ? (
        <div className="loading">Loading...</div>
      ) : (
        <>
          {/* Unmapped Identifiers */}
          {unmapped.length > 0 && (
            <div style={{ background: '#fff', borderRadius: 12, padding: 24, border: '1px solid #e8e8e8', marginBottom: 24 }}>
              <h3 style={{ margin: '0 0 4px 0', color: 'var(--text-heading)', fontSize: 16 }}>
                Unmapped Identifiers
              </h3>
              <p style={{ margin: '0 0 16px 0', fontSize: 13, color: '#888' }}>
                {unmapped.length} identifier{unmapped.length !== 1 ? 's' : ''} found in recordings with no agent mapping.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {unmapped.map(id => (
                  <div key={id} style={{ position: 'relative' }}>
                    <div
                      style={{
                        display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
                        padding: '8px 12px', borderRadius: 8,
                        background: '#f5f5f5', border: '1px solid #ddd',
                        fontSize: 13,
                      }}
                    >
                      <span style={{ fontFamily: 'monospace', flex: '0 0 auto' }}>{id}</span>
                      <span style={{
                        fontSize: 10, padding: '1px 6px', borderRadius: 8, flex: '0 0 auto',
                        background: TYPE_COLORS[guessAliasType(id)]?.bg || '#f5f5f5',
                        color: TYPE_COLORS[guessAliasType(id)]?.text || '#666',
                      }}>
                        {guessAliasType(id)}
                      </span>
                      <select
                        value=""
                        onChange={e => { if (e.target.value) handleCreateAlias(id, e.target.value); }}
                        style={{
                          padding: '5px 10px', border: '1px solid #ccc', borderRadius: 6,
                          fontSize: 12, background: '#fff', cursor: 'pointer', flex: '0 0 auto',
                        }}
                      >
                        <option value="">Assign to agent…</option>
                        {sortedAgents.map(a => (
                          <option key={a.canonicalName} value={a.canonicalName}>{a.canonicalName}</option>
                        ))}
                      </select>
                      <button
                        onClick={() => togglePreview(id)}
                        style={{
                          background: previewingId === id ? '#00bcd4' : 'none',
                          color: previewingId === id ? '#fff' : '#00bcd4',
                          border: '1px solid #00bcd4',
                          cursor: 'pointer', fontSize: 11, padding: '4px 10px', borderRadius: 6, flex: '0 0 auto',
                        }}
                      >
                        {previewingId === id ? 'Hide Calls' : 'Listen'}
                      </button>
                      <button
                        onClick={() => { setCreatingFrom(id); setNewAgentName(guessAliasType(id) === 'name' ? titleCase(id) : ''); setNewAgentAliasType(guessAliasType(id)); }}
                        style={{
                          background: 'none', border: '1px solid #00bcd4', color: '#00bcd4',
                          cursor: 'pointer', fontSize: 11, padding: '4px 10px', borderRadius: 6, flex: '0 0 auto',
                        }}
                      >
                        + New Agent
                      </button>
                    </div>

                    {previewingId === id && (
                      <div style={{
                        marginTop: 6, marginLeft: 8, padding: '10px 12px',
                        background: '#fafafa', border: '1px solid #e0e0e0', borderRadius: 6,
                        fontSize: 12,
                      }}>
                        {previewLoading ? (
                          <div style={{ color: '#888' }}>Loading recent calls…</div>
                        ) : previewRecordings.length === 0 ? (
                          <div style={{ color: '#888' }}>No recordings found for this identifier.</div>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            <div style={{ color: '#666', fontSize: 11, marginBottom: 2 }}>
                              {previewRecordings.length} most recent recording{previewRecordings.length !== 1 ? 's' : ''} — click a row to listen and identify the agent
                            </div>
                            {previewRecordings.map((r, i) => (
                              <div
                                key={`${r.PK}-${r.SK}`}
                                onClick={() => setSelectedRecording(r)}
                                style={{
                                  display: 'flex', alignItems: 'center', gap: 12,
                                  padding: '6px 10px', background: '#fff',
                                  border: '1px solid #e0e0e0', borderRadius: 6,
                                  cursor: 'pointer',
                                }}
                                onMouseEnter={e => { e.currentTarget.style.borderColor = '#00bcd4'; e.currentTarget.style.background = '#e0f7fa'; }}
                                onMouseLeave={e => { e.currentTarget.style.borderColor = '#e0e0e0'; e.currentTarget.style.background = '#fff'; }}
                              >
                                <span style={{ color: '#00bcd4', fontSize: 14 }}>▶</span>
                                <span style={{ minWidth: 90, color: '#555' }}>{r.recordDate || '-'}</span>
                                <span style={{ minWidth: 110, fontFamily: 'monospace' }}>{formatPhone(r)}</span>
                                <span style={{ minWidth: 50, color: '#888' }}>{formatDuration(r.duration || 0)}</span>
                                <span style={{ flex: 1, fontSize: 10, color: '#999', fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {r.systemId} · {(r.originalFilename || '').slice(0, 40)}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Inline new agent creation */}
                    {creatingFrom === id && (
                      <div style={{
                        position: 'absolute', top: '100%', left: 0, marginTop: 4, zIndex: 10,
                        background: '#fff', borderRadius: 8, padding: 12, border: '1px solid #ddd',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)', minWidth: 240,
                      }}>
                        <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>New agent for "{id}"</div>
                        <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                          <input
                            autoFocus
                            value={newAgentName}
                            onChange={e => setNewAgentName(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') handleCreateAgent(id); if (e.key === 'Escape') setCreatingFrom(null); }}
                            placeholder="Agent name (e.g. Ryan Ditsch)"
                            style={{ flex: 1, padding: '6px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13 }}
                          />
                        </div>
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                          <select value={newAgentAliasType} onChange={e => setNewAgentAliasType(e.target.value)}
                            style={{ padding: '6px 8px', border: '1px solid #ddd', borderRadius: 6, fontSize: 12, background: '#fff' }}>
                            {ALIAS_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                          </select>
                          <button onClick={() => handleCreateAgent(id)}
                            style={{ padding: '6px 12px', background: '#00bcd4', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>
                            Create
                          </button>
                          <button onClick={() => setCreatingFrom(null)}
                            style={{ padding: '6px 8px', background: 'none', border: '1px solid #ddd', borderRadius: 6, fontSize: 12, cursor: 'pointer', color: '#666' }}>
                            x
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Mapped Agents */}
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, border: '1px solid #e8e8e8' }}>
            <h3 style={{ margin: '0 0 16px 0', color: 'var(--text-heading)', fontSize: 16 }}>
              Mapped Agents {agents.length > 0 && <span style={{ color: '#999', fontWeight: 400 }}>({agents.length})</span>}
            </h3>

            {agents.length === 0 && unmapped.length === 0 && (
              <p style={{ color: '#999', fontSize: 14 }}>No agent identifiers found in recordings yet.</p>
            )}
            {agents.length === 0 && unmapped.length > 0 && (
              <p style={{ color: '#999', fontSize: 14 }}>No agents mapped yet. Create one from the unmapped identifiers above.</p>
            )}

            {sortedAgents.length > 0 && (
              <table className="data-table">
                <thead>
                  <tr>
                    <th style={{ width: '22%' }}>Agent</th>
                    <th style={{ width: 70, textAlign: 'center' }}>#</th>
                    <th>Identifiers</th>
                    <th style={{ width: 90, textAlign: 'right' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {sortedAgents.map(agent => (
                    <React.Fragment key={agent.canonicalName}>
                      <tr>
                        <td style={{ fontWeight: 600, color: 'var(--text-heading)' }}>{agent.canonicalName}</td>
                        <td style={{ textAlign: 'center', color: '#888', fontSize: 12 }}>{agent.aliases.length}</td>
                        <td>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                            {agent.aliases.map(a => {
                              const colors = TYPE_COLORS[a.aliasType] || TYPE_COLORS.other;
                              return (
                                <span key={a.aliasValue} style={{
                                  display: 'inline-flex', alignItems: 'center', gap: 4,
                                  padding: '2px 8px', borderRadius: 10, fontSize: 11,
                                  background: colors.bg, color: colors.text, fontFamily: 'monospace',
                                }}>
                                  {a.aliasValue}
                                  <span style={{ fontSize: 9, opacity: 0.7 }}>({ALIAS_TYPES.find(t => t.value === a.aliasType)?.label || a.aliasType})</span>
                                  <button
                                    onClick={() => { if (window.confirm(`Remove alias "${a.aliasValue}" from ${agent.canonicalName}?`)) handleDeleteAlias(a.aliasValue); }}
                                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: colors.text, opacity: 0.5, fontSize: 12, padding: '0 0 0 2px', lineHeight: 1 }}
                                    title="Remove alias"
                                  >&times;</button>
                                </span>
                              );
                            })}
                          </div>
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <button
                            onClick={() => { setAddAliasFor(addAliasFor === agent.canonicalName ? null : agent.canonicalName); setManualAlias(''); setManualAliasType('other'); }}
                            style={{
                              background: addAliasFor === agent.canonicalName ? '#00bcd4' : 'none',
                              color: addAliasFor === agent.canonicalName ? '#fff' : '#00bcd4',
                              border: '1px solid #00bcd4', borderRadius: 6,
                              padding: '3px 10px', fontSize: 11, cursor: 'pointer',
                            }}
                          >
                            {addAliasFor === agent.canonicalName ? 'Cancel' : '+ Alias'}
                          </button>
                        </td>
                      </tr>
                      {addAliasFor === agent.canonicalName && (
                        <tr style={{ background: '#fafafa' }}>
                          <td colSpan={4} style={{ padding: '8px 12px' }}>
                            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                              <span style={{ fontSize: 11, color: '#888', minWidth: 130 }}>Add alias for {agent.canonicalName}:</span>
                              <input
                                autoFocus
                                value={manualAlias}
                                onChange={e => setManualAlias(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter') handleAddManualAlias(agent.canonicalName); if (e.key === 'Escape') setAddAliasFor(null); }}
                                placeholder="Identifier value"
                                style={{ flex: 1, padding: '5px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 12 }}
                              />
                              <select value={manualAliasType} onChange={e => setManualAliasType(e.target.value)}
                                style={{ padding: '5px 8px', border: '1px solid #ddd', borderRadius: 6, fontSize: 12, background: '#fff' }}>
                                {ALIAS_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                              </select>
                              <button onClick={() => handleAddManualAlias(agent.canonicalName)}
                                style={{ padding: '5px 12px', background: '#00bcd4', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>
                                Add
                              </button>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {selectedRecording && (
        <RecordingDetail recording={selectedRecording} onClose={() => setSelectedRecording(null)} />
      )}
    </div>
  );
}
